import { connectWithDeadline } from "./consultationConnectDeadline.mjs";

const SDK_URL =
  "https://sdk.twilio.com/js/video/releases/2.32.1/twilio-video.min.js";

let sdkPromise = null;
const CONSULTATION_CONNECT_TIMEOUT_MS = 15_000;

export const CONSULTATION_AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};

function createSdkLoadError(message = "Consultation media is unavailable right now.") {
  const error = new Error(message);
  error.code = "consultation_media_unavailable";
  return error;
}

export async function loadManagedConsultationSdk() {
  if (typeof window === "undefined") {
    throw createSdkLoadError();
  }

  if (window.Twilio?.Video?.connect) {
    return window.Twilio.Video;
  }

  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-consultation-sdk="video"]');
      if (existing) {
        existing.addEventListener("load", () => {
          if (window.Twilio?.Video?.connect) resolve(window.Twilio.Video);
          else reject(createSdkLoadError());
        });
        existing.addEventListener("error", () => reject(createSdkLoadError()));
        return;
      }

      const script = document.createElement("script");
      script.src = SDK_URL;
      script.async = true;
      script.defer = true;
      script.dataset.consultationSdk = "video";
      script.onload = () => {
        if (window.Twilio?.Video?.connect) resolve(window.Twilio.Video);
        else reject(createSdkLoadError());
      };
      script.onerror = () => reject(createSdkLoadError());
      document.head.appendChild(script);
    }).catch((error) => {
      sdkPromise = null;
      throw error;
    });
  }

  return sdkPromise;
}

export async function connectManagedConsultationRoom({
  accessToken,
  roomName,
  kind = "audio",
  tracks = null,
}) {
  const Video = await loadManagedConsultationSdk();

  if (Video?.isSupported === false) {
    throw createSdkLoadError(
      "Consultation media is not supported on this browser."
    );
  }

  const providedTracks = Array.isArray(tracks)
    ? tracks.filter((track) => track && track.readyState !== "ended")
    : [];

  return connectWithDeadline(() => Video.connect(accessToken, {
      name: roomName,
      ...(providedTracks.length > 0
        ? { tracks: providedTracks }
        : {
            audio: CONSULTATION_AUDIO_CONSTRAINTS,
            video:
              kind === "video"
                ? {
                    facingMode: "user",
                    width: 1280,
                  }
                : false,
          }),
      dominantSpeaker: true,
      networkQuality: { local: 1, remote: 1 },
      maxAudioBitrate: 16000,
      receiveTranscriptions: true,
    }), {
      timeoutMs: CONSULTATION_CONNECT_TIMEOUT_MS,
      onTimeout: () => providedTracks.forEach((track) => track.stop?.()),
      onLateRoom: (room) => {
        releaseManagedConsultationRoomMedia(room);
        room.disconnect?.();
      },
    });
}

export function getPublishedTrack(publications = new Map(), kind = "audio") {
  const publication = Array.from(publications.values()).find(
    (entry) => entry?.track?.kind === kind
  );
  return publication?.track || null;
}

export function getLocalTrack(room, kind = "audio") {
  if (!room?.localParticipant) return null;
  const publications =
    kind === "video"
      ? room.localParticipant.videoTracks
      : room.localParticipant.audioTracks;
  return getPublishedTrack(publications, kind);
}

export function clearMediaContainer(container) {
  if (!container) return;
  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }
}

export function attachTrackToContainer(track, container, options = {}) {
  if (!track || !container) return null;

  clearMediaContainer(container);
  const mediaElement = track.attach();
  mediaElement.autoplay = true;
  mediaElement.playsInline = true;
  mediaElement.muted = Boolean(options.muted);

  if (track.kind === "video") {
    mediaElement.className =
      options.className || "h-full w-full object-cover";
  } else {
    mediaElement.className = options.className || "hidden";
  }

  container.appendChild(mediaElement);
  return mediaElement;
}

export function detachTrack(track) {
  if (!track?.detach) return;
  track.detach().forEach((element) => {
    if (element?.parentNode) {
      element.parentNode.removeChild(element);
    }
  });
}

// Room.disconnect() ends transport but does not guarantee that every
// browser-owned MediaStreamTrack is stopped. Release local tracks explicitly
// whenever AfriMed ends a consultation.
export function releaseManagedConsultationRoomMedia(room) {
  const participant = room?.localParticipant;
  if (!participant) return;

  const publications = [
    ...(participant.audioTracks ? Array.from(participant.audioTracks.values()) : []),
    ...(participant.videoTracks ? Array.from(participant.videoTracks.values()) : []),
  ];
  const releasedTracks = new Set();

  publications.forEach((publication) => {
    const track = publication?.track;
    if (!track || releasedTracks.has(track)) return;
    releasedTracks.add(track);
    try { participant.unpublishTrack?.(track); } catch { /* already unpublished */ }
    try { detachTrack(track); } catch { /* best-effort DOM cleanup */ }
    try { track.stop?.(); } catch { /* best-effort SDK cleanup */ }
    try { track.mediaStreamTrack?.stop?.(); } catch { /* already stopped */ }
  });
}

export function bindParticipantTracks(participant, handlers = {}) {
  if (!participant) return () => {};

  const onTrackSubscribed =
    handlers.onTrackSubscribed || (() => {});
  const onTrackUnsubscribed =
    handlers.onTrackUnsubscribed || (() => {});
  const publicationCleanups = [];

  const bindPublication = (publication) => {
    if (!publication) return;

    const handlePublicationSubscribed = (track) =>
      onTrackSubscribed(track, participant);
    const handlePublicationUnsubscribed = (track) =>
      onTrackUnsubscribed(track, participant);

    if (publication?.track) {
      onTrackSubscribed(publication.track, participant);
    }

    publication.on?.("subscribed", handlePublicationSubscribed);
    publication.on?.("unsubscribed", handlePublicationUnsubscribed);

    publicationCleanups.push(() => {
      publication.off?.("subscribed", handlePublicationSubscribed);
      publication.off?.("unsubscribed", handlePublicationUnsubscribed);
    });
  };

  participant.tracks?.forEach((publication) => {
    bindPublication(publication);
  });

  const handleTrackPublished = (publication) => {
    bindPublication(publication);
  };
  const handleSubscribed = (track) => onTrackSubscribed(track, participant);
  const handleUnsubscribed = (track) => onTrackUnsubscribed(track, participant);

  participant.on("trackPublished", handleTrackPublished);
  participant.on("trackSubscribed", handleSubscribed);
  participant.on("trackUnsubscribed", handleUnsubscribed);

  return () => {
    participant.off("trackPublished", handleTrackPublished);
    participant.off("trackSubscribed", handleSubscribed);
    participant.off("trackUnsubscribed", handleUnsubscribed);
    publicationCleanups.forEach((cleanup) => cleanup?.());
  };
}
